import createDOMPurify from 'dompurify';
import { DEFAULT_FONT_SIZE_CQH } from '@/helpers/preview/font-size-cqh.helper';

let domPurify: ReturnType<typeof createDOMPurify> | undefined;

/** DOMPurify default export is the sanitizer in browsers and a window factory in Node. */
function getDomPurify(): ReturnType<typeof createDOMPurify> {
  if (domPurify) {
    return domPurify;
  }
  if (typeof createDOMPurify.sanitize === 'function') {
    domPurify = createDOMPurify;
    return domPurify;
  }
  if (typeof window !== 'undefined' && window.document) {
    domPurify = createDOMPurify(window);
    return domPurify;
  }
  throw new Error('DOMPurify requires a DOM environment');
}

/** Keep in sync with 21m-event-player html-layer SANITIZE_CONFIG. */
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'div',
    'span',
    'p',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'a',
    'strong',
    'em',
    'b',
    'i',
    'u',
    'br',
    'img',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'section',
    'header',
    'footer',
    'label',
    'small',
    'sub',
    'sup',
    'figure',
    'figcaption',
    'style',
  ],
  ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'colspan', 'rowspan'],
  ADD_ATTR: ['target'],
  FORBID_TAGS: ['iframe', 'script', 'object', 'embed'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick'],
};

export function sanitizePreviewHtml(html: string): string {
  if (!html) {
    return '';
  }
  return getDomPurify().sanitize(html, SANITIZE_CONFIG);
}

/**
 * Preview font size from absolute cqh setting (default 2).
 * Pure linear scale against the given height (layer box height) — no px clamp —
 * matching player HTML layers that use `${cqh}cqh` against the layer wrapper.
 */
export function resolvePreviewHtmlFontSizePx(
  containerHeightPx: number,
  fontSizeCqh: number = DEFAULT_FONT_SIZE_CQH,
): number {
  const cqh =
    typeof fontSizeCqh === 'number' && Number.isFinite(fontSizeCqh) && fontSizeCqh > 0
      ? fontSizeCqh
      : DEFAULT_FONT_SIZE_CQH;
  if (containerHeightPx <= 0) {
    return 0;
  }
  return containerHeightPx * (cqh / 100);
}

export function resolvePreviewHtmlFontSizeCss(fontSizeCqh: number = DEFAULT_FONT_SIZE_CQH): string {
  const cqh =
    typeof fontSizeCqh === 'number' && Number.isFinite(fontSizeCqh) && fontSizeCqh > 0
      ? fontSizeCqh
      : DEFAULT_FONT_SIZE_CQH;
  return `${cqh}cqh`;
}

export function buildPreviewHtmlSrcDoc(
  sanitizedHtml: string,
  textColor: string | undefined,
  fontSizeCss: string,
): string {
  const colorRule = textColor ? `color:${textColor};` : '';
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<style>
  *, *::before, *::after { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    ${colorRule}
  }
  body {
    container-type: size;
    font-size: ${fontSizeCss};
    line-height: 1.2;
  }
  img { max-width: 100%; height: auto; }
</style>
</head>
<body>${sanitizedHtml}</body>
</html>`;
}
